import React, { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  CheckCircle2,
  Sparkles,
  Crown,
  ArrowRight,
  CalendarCheck,
  LayoutGrid,
  Heart,
  Pin,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

type OnboardingStep = {
  title: string;
  description: string;
  points: string[];
  accent?: "default" | "premium";
  icon: ReactNode;
};

const steps: OnboardingStep[] = [
  {
    title: "Daily Checklist",
    description: "Quick taps, instant confetti, easy day switching.",
    points: ["Tap to complete", "Jump to today", "Hide paused"],
    icon: <CalendarCheck className="h-5 w-5 text-primary" />,
  },
  {
    title: "Progress at a glance",
    description: "Contribution grid + streaks keep momentum visible.",
    points: ["Heatmap grid", "Month jump", "Streak + last 5"],
    icon: <LayoutGrid className="h-5 w-5 text-primary" />,
  },
  {
    title: "Stay organized",
    description: "Pin priorities, log mood, tidy view in seconds.",
    points: ["Pin favorites", "Mood inline", "Toggle paused"],
    icon: <Pin className="h-5 w-5 text-primary" />,
  },
  {
    title: "Unlock more with Premium",
    description: "Unlimited habits, colors, import/export, priority perks.",
    points: ["Unlimited habits", "CSV import/export", "Priority support"],
    accent: "premium",
    icon: <Crown className="h-5 w-5 text-primary" />,
  },
];

type OnboardingModalProps = {
  open: boolean;
  onComplete: () => void | Promise<void>;
  isPremium?: boolean;
};

export default function OnboardingModal({
  open,
  onComplete,
  isPremium = false,
}: OnboardingModalProps) {
  const [stepIndex, setStepIndex] = useState(0);
  const navigate = useNavigate();

  const totalSteps = steps.length;
  const currentStep = useMemo(() => steps[stepIndex], [stepIndex]);

  useEffect(() => {
    if (open) {
      setStepIndex(0);
    }
  }, [open]);

  const handleNext = () => {
    if (stepIndex < totalSteps - 1) {
      setStepIndex((prev) => prev + 1);
      return;
    }
    onComplete();
  };

  const handleBack = () => {
    if (stepIndex > 0) {
      setStepIndex((prev) => prev - 1);
    }
  };

  const handleClose = () => {
    onComplete();
  };

  const handleUpgrade = () => {
    onComplete();
    navigate("/pricing");
  };

  const renderVisual = () => {
    switch (stepIndex) {
      case 0:
        return (
          <div className="rounded-xl border bg-card p-4 flex flex-col gap-3">
            <div className="flex items-center gap-2 text-sm font-medium">
              <CalendarCheck className="h-4 w-4 text-primary" />
              Quick check-in
            </div>
            <div className="space-y-2">
              {["Water", "Focus block", "Sleep"].map((label, idx) => (
                <div
                  key={label}
                  className="flex items-center justify-between rounded-lg bg-muted/60 px-3 py-2"
                >
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <div className={`h-3 w-3 rounded-full ${idx === 0 ? "bg-primary" : idx === 1 ? "bg-primary/70" : "bg-primary/30"}`} />
                    {label}
                  </div>
                  <CheckCircle2 className="h-4 w-4 text-primary" />
                </div>
              ))}
            </div>
          </div>
        );
      case 1:
        return (
          <div className="rounded-xl border bg-card p-4">
            <div className="flex items-center gap-2 text-sm font-medium mb-3">
              <LayoutGrid className="h-4 w-4 text-primary" />
              Progress heatmap
            </div>
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: 28 }).map((_, i) => (
                <div
                  key={i}
                  className={`h-4 w-4 rounded-sm ${
                    i % 6 === 0
                      ? "bg-primary/80"
                      : i % 5 === 0
                      ? "bg-primary/60"
                      : i % 4 === 0
                      ? "bg-primary/40"
                      : "bg-muted"
                  }`}
                />
              ))}
            </div>
          </div>
        );
      case 2:
        return (
          <div className="rounded-xl border bg-card p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Heart className="h-4 w-4 text-primary" />
              Mood + focus
            </div>
            <div className="flex flex-wrap gap-2">
              {["Easy", "Meh", "Struggled"].map((mood) => (
                <Badge key={mood} variant="outline">
                  {mood}
                </Badge>
              ))}
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Pin className="h-4 w-4 text-primary" />
              Pin the habits that matter most.
            </div>
          </div>
        );
      default:
        return (
          <div className="rounded-xl border bg-gradient-to-r from-primary/15 via-background to-background p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Crown className="h-4 w-4 text-primary" />
              Premium perks
            </div>
            <div className="flex flex-wrap gap-2">
              {["Unlimited habits", "Custom colors", "CSV import/export", "Priority support"].map(
                (perk) => (
                  <Badge key={perk} variant="secondary">
                    {perk}
                  </Badge>
                )
              )}
            </div>
          </div>
        );
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-xl [&>button]:hidden">
        <DialogHeader className="space-y-1">
          <div className="flex items-center justify-between">
            <Badge variant="secondary">
              Step {stepIndex + 1} of {totalSteps}
            </Badge>
            {currentStep.accent === "premium" && (
              <Badge className="flex items-center gap-1" variant="outline">
                <Crown className="h-3 w-3 text-primary" />
                Premium
              </Badge>
            )}
          </div>
          <DialogTitle className="text-xl flex items-center gap-2">
            <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center">
              {currentStep.icon}
            </div>
            {currentStep.title}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {currentStep.description}
          </DialogDescription>
          <Progress
            value={((stepIndex + 1) / totalSteps) * 100}
            className="h-2"
          />
        </DialogHeader>

        <div className="space-y-4">
          {renderVisual()}

          <div className="flex flex-wrap gap-2">
            {currentStep.points.map((point, index) => (
              <Badge key={index} variant="outline" className="text-xs">
                {point}
              </Badge>
            ))}
          </div>

          {currentStep.accent === "premium" && !isPremium && (
            <div className="rounded-md border p-3 bg-muted/50 flex gap-2">
              <Crown className="h-5 w-5 text-primary" />
              <div className="space-y-1">
                <p className="text-sm font-medium">Ready to upgrade?</p>
                <p className="text-xs text-muted-foreground">
                  Unlock unlimited habits, custom colors, and effortless CSV
                  import/export to keep your data portable.
                </p>
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button size="sm" onClick={handleUpgrade}>
                    View plans
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setStepIndex(Math.max(stepIndex - 1, 0))}
                  >
                    Review features again
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between pt-2">
          <Button variant="ghost" onClick={handleClose}>
            Skip for now
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleBack} disabled={stepIndex === 0}>
              Back
            </Button>
            <Button onClick={handleNext}>
              {stepIndex === totalSteps - 1 ? "Get started" : "Next"}
              {stepIndex !== totalSteps - 1 && (
                <ArrowRight className="h-4 w-4 ml-1" />
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

